import { normalizeError } from 'core/common/errors';
import { SpanAttributes, SpanName } from 'core/common/observability/entities';
import { ATTRS_ANALYTICS_EVENT_NAME } from 'core/common/observability/entities/SemanticAttributes';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { ErrorMonitoring } from 'core/common/services/errorMonitoring';
import { TaboolaService } from '../../interfaces';
import { TaboolaServiceDecorator } from './TaboolaServiceDecorator';

export class TaboolaServiceWithMonitoring extends TaboolaServiceDecorator {
  constructor(
    taboolaService: TaboolaService,
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly errorMonitoringService: ErrorMonitoring,
    private readonly receiveAdditionalObservabilityAttributes?: () => SpanAttributes,
  ) {
    super(taboolaService);
  }

  track(name: string, params?: Record<string, unknown>) {
    if (!super.isTfaRequired()) {
      try {
        return super.track(name, params);
      } catch {
        this.errorMonitoringService.captureError('Taboola track failed', {
          tags: {
            error_message: 'Taboola track failed',
            code: 'taboola_track_failed_when_not_required',
          },
        });
        return;
      }
    }

    if (super.isTfaRequired() && !super.isTfaAvailable()) {
      try {
        return super.track(name, params);
      } catch {
        this.errorMonitoringService.captureError('Taboola track failed', {
          tags: {
            error_message: 'Taboola track failed',
            code: 'taboola_track_failed_when_required_not_available',
          },
        });
        return;
      }
    }

    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_TABOOLA);
    const spanAttributes = {
      [ATTRS_ANALYTICS_EVENT_NAME]: name,
      ...(this.receiveAdditionalObservabilityAttributes
        ? this.receiveAdditionalObservabilityAttributes()
        : {}),
    };
    span.setAttributes(spanAttributes);

    try {
      const result = super.track(name, params);
      span.succeed();
      return result;
    } catch (err) {
      const error = normalizeError(err);
      span.fail(error);
      return;
    }
  }
}
